import Info from 'components/form/Info';
import CountryInput from 'components/form/Inputs/CountryInput';
import Textarea from 'components/form/Inputs/Textarea';
import ProfessionalSituationField from 'components/form/ProfessionalSituationField';
import GENDERS, { GenderValues } from 'enums/Genders';
import REGIONS from 'enums/Regions';
import { ProfessionalSituationValues } from 'enums/ProfessionalSituation';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import Error from '../form/Error';
import { FlexColumn } from '../form/generic';
import Input from '../form/Inputs/Input';
import LabelledCheckbox from '../form/Inputs/LabelledCheckbox';
import Select from '../form/Inputs/Select';
import MailIcon from '../icons/Mail';
import Field from '../profileNavigation/Field';

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  max-width: 60rem;
  button {
    margin-left: 0.5rem;
  }
`;

export type State = {
  gender: GenderValues;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  region: string;
  country: string;
  isMetropolitan: boolean;
  dateOfBirth: string;

  /** Complément en texte libre de la situation actuelle (mentors). */
  organism: string;
  professionalSituation: ProfessionalSituationValues | '';
  priority: boolean;
  presentation: string;
  comments: string;
};

export type Errors = {
  general: boolean;
  email: boolean;
  phone: boolean;
  professionalSituation: boolean;
  organism: boolean;
  address: boolean;
};

export const initialErrors: Errors = {
  general: false,
  email: false,
  phone: false,
  professionalSituation: false,
  organism: false,
  address: false,
};

type Variant = 'young' | 'mentor' | 'volunteer;';

type Props = {
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
  errors: Errors;
  setErrors: React.Dispatch<React.SetStateAction<Errors>>;
  loading: boolean;
  variant?: Variant;
  adminView?: boolean;
  onSubmit: React.FormEventHandler;
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
};
const UserInformationForm: FC<Props> = ({
  state,
  setState,
  variant,
  adminView,
  errors,
  loading,
  onSubmit,
  onCancel,
}) => {
  const setCountry = useCallback(
    (value: string) => {
      setState({
        ...state,
        country: value,
        isMetropolitan: value === 'France' ? state.isMetropolitan : false,
      });
    },
    [state, setState],
  );

  const handleChangeInput: React.ChangeEventHandler<
    HTMLInputElement & HTMLTextAreaElement
  > = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e,
  ) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const handleChangeCheckbox: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    if (e.target.name === 'isMetropolitan') {
      return setState({
        ...state,
        isMetropolitan: e.target.checked,
        country: e.target.checked ? 'France' : state.country,
      });
    }
    setState({
      ...state,
      [e.target.name]: e.target.checked,
    });
  };

  // Adresse complète obligatoire pour les mentorés (hors vue admin).
  const requireAddress = variant === 'young' && !adminView;
  const requiredMark = requireAddress ? ' *' : '';

  return (
    <section>
      {errors.general && (
        <Error style={{ marginBottom: '1rem' }}>Une erreur est survenue.</Error>
      )}
      <form onSubmit={onSubmit}>
        {variant === 'mentor' && adminView && (
          <Field label="Prioritaire">
            <LabelledCheckbox
              label="Ce mentor est prioritaire"
              name="priority"
              checked={state.priority}
              onChange={handleChangeCheckbox}
            />
          </Field>
        )}

        <Field label="Genre">
          <Select
            value={state.gender}
            onChange={handleSelectChange}
            name="gender"
          >
            <option value={'MALE' as GenderValues}>{GENDERS['MALE']}</option>
            <option value={'FEMALE' as GenderValues}>
              {GENDERS['FEMALE']}
            </option>
            <option value={'NOT_SPECIFIED' as GenderValues}>
              {GENDERS['NOT_SPECIFIED']}
            </option>
          </Select>
        </Field>

        <Field label="Nom">
          <Input
            value={state.lastName}
            onChange={handleChangeInput}
            name="lastName"
            required={!adminView}
          />
        </Field>

        <Field label="Prénom">
          <Input
            value={state.firstName}
            onChange={handleChangeInput}
            name="firstName"
            required={!adminView}
          />
        </Field>

        <Field label="Adresse email">
          <Input
            icon={<MailIcon />}
            value={state.email}
            onChange={handleChangeInput}
            name="email"
            type="email"
            required
          />
        </Field>

        {!!adminView && (
          <Field label="Commentaires">
            <FlexColumn gap={0.5}>
              <Info>
                Ce champ ne peut être vu et modifié que par l'administrateur.
              </Info>
              <Textarea
                name="comments"
                value={state.comments}
                onChange={handleChangeInput}
                noEnterFireSubmit
                rows={10}
              />
            </FlexColumn>
          </Field>
        )}

        {variant === 'mentor' && (
          <Field label="Situation actuelle *">
            <ProfessionalSituationField
              label={null}
              value={state.professionalSituation}
              details={state.organism}
              onChange={(professionalSituation, organism) =>
                setState({ ...state, professionalSituation, organism })
              }
              situationError={errors.professionalSituation}
              detailsError={errors.organism}
            />
          </Field>
        )}

        <Field label="Date de naissance">
          <Input
            type="date"
            value={state.dateOfBirth}
            onChange={handleChangeInput}
            name="dateOfBirth"
            required={!adminView}
            placeholder="aaaa-mm-jj (ex: 2022-05-19)"
          />
        </Field>

        <Field label="Numéro de téléphone">
          <Input
            value={state.phone}
            onChange={handleChangeInput}
            name="phone"
            required={!adminView}
          />
        </Field>

        {errors.address && (
          <Error style={{ marginBottom: '1rem' }}>
            Veuillez renseigner votre adresse complète (adresse, région, code
            postal, ville et pays).
          </Error>
        )}

        <Field label={`Adresse${requiredMark}`}>
          <Input
            value={state.address}
            onChange={handleChangeInput}
            name="address"
            placeholder="Numéro et rue"
            required={requireAddress}
          />
        </Field>

        <Field label={`Code postal et ville${requireAddress ? ' *' : ''}`}>
          <Input
            value={state.postalCode || ''}
            onChange={handleChangeInput}
            name="postalCode"
            placeholder="Code postal"
            required={!adminView}
          />
          <Input
            value={state.city}
            onChange={handleChangeInput}
            name="city"
            placeholder="Ville"
            required={requireAddress}
          />
        </Field>

        <Field label={`Région${requiredMark}`}>
          <Select
            value={state.region || ''}
            onChange={handleSelectChange}
            name="region"
            required={!adminView}
          >
            <option value="">-- Sélectionnez une région --</option>
            {/* Valeur historique saisie en texte libre, absente de la liste */}
            {!!state.region &&
              !(REGIONS as readonly string[]).includes(state.region) && (
                <option value={state.region}>{state.region}</option>
              )}
            {REGIONS.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={`Pays${requiredMark}`}>
          <FlexColumn gap={1}>
            <CountryInput
              value={state.country}
              setValue={setCountry}
              required={requireAddress}
            />
            <LabelledCheckbox
              label="Je vis en France métropolitaine"
              checked={state.isMetropolitan}
              onChange={handleChangeCheckbox}
              name="isMetropolitan"
            />
          </FlexColumn>
        </Field>
        <Field label="Mon profil">
          <FlexColumn gap={0.5}>
            <Info>Ce champ peut être vu par d'autres utilisateurs.</Info>
            <Textarea
              rows={15}
              placeholder="Décrivez-vous en quelques lignes"
              onChange={handleChangeInput}
              value={state.presentation}
              name="presentation"
              characterLimit={1500}
              noEnterFireSubmit
            />
          </FlexColumn>
        </Field>

        <ButtonContainer>
          <Button variant="outlined" onClick={onCancel}>
            Annuler
          </Button>
          <Button type="submit" color="primary" loading={loading}>
            Enregistrer
          </Button>
        </ButtonContainer>
      </form>
    </section>
  );
};

export default UserInformationForm;
